import { Inject, Injectable, Optional, PLATFORM_ID, inject } from "@angular/core";
import { take } from 'rxjs/operators';
import {
  Resolve,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  ActivatedRoute,
} from "@angular/router";
import { firstValueFrom, map } from "rxjs";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { Meta, Title } from "@angular/platform-browser";
import { SeoService } from "src/app/services/seo.service";
import { isPlatformServer } from "@angular/common";
import { buildSEOImageUrl } from "src/app/utils/seo-image.helper";

@Injectable({ providedIn: "root" })
export class IllustrationCollectionResolver implements Resolve<any> {
  afs = inject(AngularFirestore);
  id = "";
  cate = "";
  collectionData: any = {};
  collectionsFiles: any = [];
  constructor(
    private meta: Meta, // private socialService: SocialService,
    private title: Title,
    public router: Router,
    private seo: SeoService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }
  async resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<any> {
    const slug = route.paramMap.get("slug");
    //console.log("Slug:", slug);
    const ref = this.afs.collection("collectionMaster", (ref) =>
      ref.where("slug", "==", slug).orderBy("createdOn", "desc")
    );
    const data$ = ref.valueChanges().pipe(
      map((res) => {
        this.populateMetaTags(res[0], route);
        return res[0];
      })
    ); // Get single item

    return await firstValueFrom(data$);
  }


  populateMetaTags(data, route) {
    //console.log(route.paramMap, data);
    const title = data.collectionName;
    const desc =
      data.desc ||
      "Illusbook offers a collection of stunning illustrations. Browse and download the perfect illustrations for your projects today.";
    const image = buildSEOImageUrl(data.thumbUrl);
    //console.log(
    //   "Router URL:",
    //   "https://illusbook.com/illustration-collection/" + data.slug
    // );
    //console.log("Router URL:", this.router);
    // const protocol =
    //   this.request.headers["x-forwarded-proto"] || this.request.protocol;
    // const host = this.request.headers.host;
    // const originalUrl = this.request.originalUrl;
    // const fullURL = `${protocol}://${host}${originalUrl}`;
    const fullURL = `https://illusbook.com/illustration-collection/${data.slug}`;
    //console.log("Window URL:", fullURL);
    this.title.setTitle(`${title} | Illusbook`);
    this.setMetaTag("name", "title", title);
    this.setMetaTag("name", "description", desc);
    this.setMetaTag("name", "robots", "index, follow, max-image-preview:large");
    this.setMetaTag("property", "og:title", title);
    this.setMetaTag("property", "og:description", desc);
    this.setMetaTag("property", "og:image", image);
    this.setMetaTag("property", "og:image:width", "1200");
    this.setMetaTag("property", "og:image:height", "1200");
    this.setMetaTag("property", "og:image:type", "image/jpeg");
    this.setMetaTag("property", "og:url", fullURL);
    this.setMetaTag("property", "og:type", "website");
    this.setMetaTag("property", "og:site_name", "Illusbook");
    this.setMetaTag("name", "twitter:card", "summary_large_image");
    this.setMetaTag("name", "twitter:title", title);
    this.setMetaTag("name", "twitter:description", desc);
    this.setMetaTag("name", "twitter:image", image);

        const ORGANIZATION_SCHEMA = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": "https://illusbook.com/#organization",
      "name": "Illusbook",
      "url": "https://illusbook.com",
      "logo": "https://illusbook.com/assets/favicon/android-icon-192x192.png",
      "description": "Illusbook offers a collection of stunning illustrations. Browse and download the perfect illustrations for your projects today.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "email": "support@illusbook.com",
        "availableLanguage": ["English"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/illusbook/",
        "https://www.instagram.com/illusbook/",
        "https://x.com/illusbook",
        "https://www.pinterest.com/illusbook/"
      ]
    };

    // 🔥 Build image gallery for rich results
    this.afs.collection("filesMaster", ref =>
      ref.where("collections", "==", data.collectionName)
        .where("status", "==", "Published")
        .orderBy("createdOn", "desc")
        .limit(6) // 3–5 is ideal; 6 gives Google choice
    ).valueChanges().pipe(take(1)).subscribe((images: any[]) => {

      const galleryImages = images
        .map(item => {
          const img = buildSEOImageUrl(item.previewUrl || item.jpeg || item.png);
          if (!img) return null;

          return {
            "@type": "ImageObject",
            contentUrl: img,
            thumbnailUrl: img,
            name: item.title || title,
            "license": "https://illusbook.com/license",
            "acquireLicensePage": "https://illusbook.com/pricing",
            "creditText": "Designed by Illusbook",
            creator: {
              "@type": "Organization",
              name: "Illusbook",
            },
            "copyrightNotice": "Illusbook",
          };
        })
        .filter(Boolean);

    // Collection Schema
    const COLLECTION_SCHEMA = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: desc,
      url: fullURL,
      primaryImageOfPage: {
        "@type": "ImageObject",
        contentUrl: image,
        url: image,
        representativeOfPage: true,
          width: 1200,
          height: 1200
      },
      mainEntity: {
        "@type": "ImageGallery",
        associatedMedia: galleryImages
      },
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://illusbook.com" },
          { "@type": "ListItem", position: 2, name: "Illustration Collection", item: fullURL }
        ]
      },
      publisher: { "@id": "https://illusbook.com/#organization" },
      isPartOf: { "@id": "https://illusbook.com/#website" },
      inLanguage: "en"
    };

      this.seo.setJSONLD([COLLECTION_SCHEMA,
      ORGANIZATION_SCHEMA
    ]);
    });


  }

  setMetaTag(
    attrName: "name" | "property",
    attrValue: string,
    content: string
  ) {
    const selector = `${attrName}="${attrValue}"`;
    //console.log(selector);
    this.meta.removeTag(selector);
    this.meta.addTag({ [attrName]: attrValue, content });
    // this.meta.updateTag({ [attrName]: attrValue, content });
  }
}
